import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { GrantCreditsDialog } from "./GrantCreditsDialog";

type Props = { userId: string };

type Grant = {
  id: string;
  amount: number;
  balance_after: number;
  created_at: string;
  category: string;
  reason: string;
  granted_by_user_id: string | null;
  reversed: boolean;
  reversed_at: string | null;
};

type ListResponse = { balance: number; grants: Grant[] };

const CATEGORY_LABEL: Record<string, string> = {
  customer_service: "Customer service",
  thank_you: "Thank you",
  gift: "Gift",
  promo: "Promotional",
  other: "Other",
};

const CATEGORY_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  customer_service: "secondary",
  thank_you: "default",
  gift: "default",
  promo: "outline",
  other: "outline",
};

export const CreditGrantsPanel = ({ userId }: Props) => {
  const qc = useQueryClient();
  const [grantOpen, setGrantOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState<Grant | null>(null);
  const [reverseReason, setReverseReason] = useState("");

  const { data, isLoading, error } = useQuery<ListResponse>({
    queryKey: ["admin-credit-grants", userId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-grant-credits", {
        body: { op: "list", user_id: userId, limit: 20 },
      });
      if (error) throw new Error(error.message);
      return data as ListResponse;
    },
  });

  const reverseMutation = useMutation({
    mutationFn: async (args: { ledgerId: string; reason: string }) => {
      const { data, error } = await supabase.functions.invoke("admin-grant-credits", {
        body: {
          op: "reverse",
          user_id: userId,
          ledger_id: args.ledgerId,
          reason: args.reason,
        },
      });
      if (error) throw new Error(error.message);
      const payload = data as { error?: string };
      if (payload?.error) throw new Error(payload.error);
      return data;
    },
    onSuccess: () => {
      toast.success("Grant reversed");
      setReverseTarget(null);
      setReverseReason("");
      qc.invalidateQueries({ queryKey: ["admin-credit-grants", userId] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not reverse grant"),
  });

  return (
    <section className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <div className="text-sm font-medium">Credit grants</div>
          <div className="text-xs text-muted-foreground">
            Granted credits never expire and roll over with the user's other balance.
          </div>
        </div>
        <Button size="sm" onClick={() => setGrantOpen(true)}>
          <Sparkles className="mr-1 h-4 w-4" /> Grant credits
        </Button>
      </div>

      {isLoading && <Skeleton className="h-24 w-full" />}

      {error && (
        <p className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
          {(error as Error).message}
        </p>
      )}

      {data && (
        <>
          <div className="flex items-baseline justify-between rounded-md bg-muted/40 px-3 py-2">
            <span className="text-xs text-muted-foreground">Current rollover balance</span>
            <span className="text-base font-semibold">
              {data.balance.toLocaleString()} credits
            </span>
          </div>

          {data.grants.length === 0 ? (
            <p className="text-xs text-muted-foreground">No admin grants yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.grants.map((g) => (
                <li
                  key={g.id}
                  className="flex items-start justify-between gap-3 rounded-md border border-border/70 p-2 text-xs"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">
                        +{g.amount.toLocaleString()} credits
                      </span>
                      <Badge variant={CATEGORY_VARIANT[g.category] ?? "outline"}>
                        {CATEGORY_LABEL[g.category] ?? g.category}
                      </Badge>
                      {g.reversed && <Badge variant="destructive">Reversed</Badge>}
                      <span className="text-muted-foreground">
                        {new Date(g.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-muted-foreground line-clamp-2">{g.reason}</p>
                  </div>
                  {!g.reversed && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReverseTarget(g);
                        setReverseReason("");
                      }}
                    >
                      <Undo2 className="mr-1 h-3.5 w-3.5" /> Reverse
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <GrantCreditsDialog
        open={grantOpen}
        onOpenChange={setGrantOpen}
        userId={userId}
        onGranted={() => qc.invalidateQueries({ queryKey: ["admin-credit-grants", userId] })}
      />

      <AlertDialog
        open={reverseTarget !== null}
        onOpenChange={(o) => {
          if (!o) {
            setReverseTarget(null);
            setReverseReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse this grant?</AlertDialogTitle>
            <AlertDialogDescription>
              This subtracts {reverseTarget?.amount.toLocaleString()} credits from the user's
              balance. Blocked if they've already spent more than this from their rollover
              balance. The original grant row is kept for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reverse-reason" className="text-xs">
              Reason for reversal (required)
            </Label>
            <Textarea
              id="reverse-reason"
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="Why are you reversing this grant?"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverseMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reverseReason.trim().length < 5 || reverseMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!reverseTarget) return;
                reverseMutation.mutate({
                  ledgerId: reverseTarget.id,
                  reason: reverseReason.trim(),
                });
              }}
            >
              {reverseMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Reverse grant"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
